import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  DocumentExtractAuthResponse,
  DocumentExtractResponse,
  DocumentExtractToken,
  DocumentExtractError
} from '../models/document-extract/document-extract.model';
import {
  DOCUMENT_EXTRACT_ENDPOINTS,
  TOKEN_VALIDITY_MS,
  DOCUMENT_EXTRACT_ERRORS,
  HTTP_STATUS
} from '../constants/document-extract.constants';

/**
 * DocumentExtractApiService
 * 
 * Low-level HTTP service for interacting with the Document Extract API.
 * Handles authentication, token caching, and document extraction requests.
 * 
 * This service follows the existing architecture pattern seen in:
 * - FleetoperateApiService
 * - IncidentsApiService
 * - YardInspectionApiService
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentExtractApiService {
  private baseUrl: string;
  private cachedToken: DocumentExtractToken | null = null;

  constructor(
    private readonly http: HttpClient,
    @Inject('environmentData') private environment: any
  ) {
    this.baseUrl = this.environment.documentExtractApiUrl;
  }

  /**
   * Authenticates with the Document Extract API and returns a JWT token
   * 
   * @returns Observable<string> The authentication token
   */
  private authenticate(): Observable<string> {
    if (!this.baseUrl) {
      return throwError(() => this.createError(DOCUMENT_EXTRACT_ERRORS.API_URL_NOT_CONFIGURED, HTTP_STATUS.INTERNAL_SERVER_ERROR));
    }

    const authUrl = `${this.baseUrl}${DOCUMENT_EXTRACT_ENDPOINTS.AUTH}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<DocumentExtractAuthResponse>(authUrl, {}, { headers }).pipe(
      map((response: DocumentExtractAuthResponse) => {
        // Cache the token with expiry time (55 minutes from now)
        this.cachedToken = {
          token: response.token,
          expiresAt: Date.now() + TOKEN_VALIDITY_MS
        };
        return response.token;
      }),
      catchError((error: HttpErrorResponse) => {
        this.clearTokenCache();
        return throwError(() => this.handleError(error, DOCUMENT_EXTRACT_ERRORS.AUTHENTICATION_FAILED));
      })
    );
  }

  /**
   * Gets a valid authentication token, either from cache or by authenticating
   * 
   * @returns Observable<string> A valid authentication token
   */
  private getToken(): Observable<string> {
    // Check if we have a cached token that's still valid
    if (this.cachedToken && this.isTokenValid()) {
      return of(this.cachedToken.token);
    }

    // Token is expired or doesn't exist, authenticate
    this.clearTokenCache();
    return this.authenticate();
  }

  /**
   * Checks if the cached token is still valid
   * 
   * @returns boolean True if token is valid, false otherwise
   */
  private isTokenValid(): boolean {
    if (!this.cachedToken) {
      return false;
    }
    return Date.now() < this.cachedToken.expiresAt;
  }

  /**
   * Clears the cached authentication token
   */
  private clearTokenCache(): void {
    this.cachedToken = null;
  }

  /**
   * Extracts data from a document using the Document Extract API
   * 
   * @param file The file to extract data from
   * @param schema The extraction schema defining what fields to extract
   * @param docType Optional document type identifier
   * @param dropdownOptions Optional dropdown options for semantic matching
   * @returns Observable<DocumentExtractResponse> The extracted data
   */
  extractDocument(
    file: File,
    schema: Record<string, string>,
    docType?: string,
    dropdownOptions?: Record<string, string[]>,
    formatInstructions?: Record<string, string>
  ): Observable<DocumentExtractResponse> {
    if (!file) {
      return throwError(() => this.createError(DOCUMENT_EXTRACT_ERRORS.NO_FILE_PROVIDED, HTTP_STATUS.BAD_REQUEST));
    }

    if (!this.baseUrl) {
      return throwError(() => this.createError(DOCUMENT_EXTRACT_ERRORS.API_URL_NOT_CONFIGURED, HTTP_STATUS.INTERNAL_SERVER_ERROR));
    }

    return this.getToken().pipe(
      switchMap((token: string) => {
        const extractUrl = `${this.baseUrl}${DOCUMENT_EXTRACT_ENDPOINTS.EXTRACT}`;
        
        // Build FormData
        const formData = new FormData();
        formData.append('schema', JSON.stringify(schema));
        
        if (docType) {
          formData.append('doc_type', docType);
        }
        
        if (dropdownOptions) {
          formData.append('dropdown_options', JSON.stringify(dropdownOptions));
        }
        
        if (formatInstructions) {
          formData.append('format_instructions', JSON.stringify(formatInstructions));
        }
        
        formData.append('file', file);

        // Set headers with authentication token
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });

        return this.http.post<DocumentExtractResponse>(extractUrl, formData, { headers }).pipe(
          catchError((error: HttpErrorResponse) => {
            // If we get a 401, the token might have expired mid-flight
            if (error.status === HTTP_STATUS.UNAUTHORIZED) {
              this.clearTokenCache();
              // Retry once with a fresh token
              return this.getToken().pipe(
                switchMap((newToken: string) => {
                  const retryHeaders = new HttpHeaders({
                    'Authorization': `Bearer ${newToken}`
                  });
                  return this.http.post<DocumentExtractResponse>(extractUrl, formData, { headers: retryHeaders });
                }),
                catchError((retryError: HttpErrorResponse) => {
                  return throwError(() => this.handleError(retryError, DOCUMENT_EXTRACT_ERRORS.EXTRACTION_FAILED));
                })
              );
            }
            return throwError(() => this.handleError(error, DOCUMENT_EXTRACT_ERRORS.EXTRACTION_FAILED));
          })
        );
      })
    );
  }

  /**
   * Checks the health of the Document Extract API
   * 
   * @returns Observable<any> Health check response
   */
  checkHealth(): Observable<any> {
    if (!this.baseUrl) {
      return throwError(() => this.createError(DOCUMENT_EXTRACT_ERRORS.API_URL_NOT_CONFIGURED, HTTP_STATUS.INTERNAL_SERVER_ERROR));
    }

    const healthUrl = this.baseUrl.replace('/api/v1', '') + DOCUMENT_EXTRACT_ENDPOINTS.HEALTH;
    
    return this.http.get(healthUrl).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => this.handleError(error, DOCUMENT_EXTRACT_ERRORS.NETWORK_ERROR));
      })
    );
  }

  /**
   * Handles HTTP errors and creates standardized error objects
   * 
   * @param error The HTTP error response
   * @param defaultMessage Default error message if none is provided
   * @returns DocumentExtractError Formatted error object
   */
  private handleError(error: HttpErrorResponse, defaultMessage: string): DocumentExtractError {
    let message = defaultMessage;
    let code = error.status;

    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      message = DOCUMENT_EXTRACT_ERRORS.NETWORK_ERROR;
    } else if (error.error?.message) {
      // Backend returned an error message
      message = error.error.message;
    } else if (error.message) {
      message = error.message;
    }

    return this.createError(message, code, error.error);
  }

  /**
   * Creates a standardized error object
   * 
   * @param message Error message
   * @param code HTTP status code
   * @param details Additional error details
   * @returns DocumentExtractError Formatted error object
   */
  private createError(message: string, code?: number, details?: any): DocumentExtractError {
    return {
      message,
      code,
      details
    };
  }

  /**
   * Clears the authentication token cache (useful for logout/session end)
   */
  public clearCache(): void {
    this.clearTokenCache();
  }
}

